import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LOGO = '/dailyreps3.png';
const ageGroups = ['U6','U7','U8','U9','U10','U11','U12','U13','U14','U15','U16','U17','U18','19+'];

export default function ClubOnboarding() {
  const navigate = useNavigate();
  const { apiFetch } = useAuth();

  // Affiliate tracking
  useEffect(() => {
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=1';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  // Wizard state
  const [step, setStep] = useState('welcome');
  const [path, setPath] = useState(null); // 'import' or 'manual'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Import path state
  const [csvText, setCsvText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Manual path state
  const [createdTeams, setCreatedTeams] = useState([]);
  const [teamForm, setTeamForm] = useState({ name: '', age_group: '', has_under_13: false });
  const [coachEmails, setCoachEmails] = useState({}); // { teamId: currentEmailInput }
  const [invitedCoaches, setInvitedCoaches] = useState({}); // { teamId: [email, ...] }
  const [activeTeamForPlayers, setActiveTeamForPlayers] = useState(null);
  const [playerForm, setPlayerForm] = useState({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
  const [addedPlayers, setAddedPlayers] = useState({}); // { teamId: [{ first_name, last_name, username }, ...] }

  // ── Completion / Skip ──

  const handleComplete = async () => {
    try {
      await apiFetch('/api/club/complete-onboarding', { method: 'POST' });
    } catch (e) { /* non-critical */ }
    navigate('/club');
  };

  // ── Import path handlers (from ClubAdmin.js) ──

  const parseCSV = (text) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return [];
    let start = 0;
    const firstLine = lines[0].toLowerCase();
    if (firstLine.includes('team') && firstLine.includes('name')) start = 1;
    return lines.slice(start).map(line => {
      const cells = line.split(',').map(c => c.trim());
      return {
        team_name: cells[0] || '',
        under_13: cells[1] || '',
        type: cells[2] || '',
        first_name: cells[3] || '',
        last_name: cells[4] || '',
        email: cells[5] || '',
      };
    });
  };

  const handleImportPreview = async () => {
    setError('');
    setImportResult(null);
    const rows = parseCSV(csvText);
    if (rows.length === 0) { setError('No data to import'); return; }
    setSaving(true);
    try {
      const data = await apiFetch('/api/club/import', {
        method: 'POST',
        body: JSON.stringify({ rows, preview: true }),
      });
      setImportPreview(data);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleImportCommit = async () => {
    setError('');
    setSaving(true);
    try {
      const rows = parseCSV(csvText);
      const data = await apiFetch('/api/club/import', {
        method: 'POST',
        body: JSON.stringify({ rows, preview: false }),
      });
      setImportResult(data);
      setImportPreview(null);
      setCsvText('');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const downloadCredentials = (credentials) => {
    const header = 'Team,First Name,Last Name,Username,Password';
    const csvRows = credentials.map(c =>
      `${c.team_name},${c.first_name},${c.last_name},${c.username},${c.password}`
    );
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'player-credentials.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  const downloadSampleCSV = () => {
    const csv = [
      'Team Name,Under 13?,Coach or Player,First Name,Last Name,Email',
      'U10 Lightning,Yes,Coach,Jane,Smith,jane.smith@example.com',
      'U10 Lightning,Yes,Player,Tommy,Johnson,parent@example.com',
      'U10 Lightning,Yes,Player,Sarah,Williams,',
      'U14 Elite,No,Coach,Mike,Davis,mike.davis@example.com',
      'U14 Elite,No,Player,Alex,Brown,',
      'U14 Elite,No,Player,Jordan,Lee,',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-import.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Manual path handlers ──

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setError('');
    if (!teamForm.name.trim()) { setError('Team name is required'); return; }
    if (!teamForm.age_group) { setError('Age group is required'); return; }
    setSaving(true);
    try {
      const data = await apiFetch('/api/club/teams', {
        method: 'POST',
        body: JSON.stringify(teamForm),
      });
      setCreatedTeams(prev => [...prev, data.team]);
      setTeamForm({ name: '', age_group: '', has_under_13: false });
      setSuccess(`Team "${data.team.name}" created.`);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleInviteCoach = async (teamId) => {
    const email = (coachEmails[teamId] || '').trim();
    if (!email) return;
    setError('');
    setSaving(true);
    try {
      const data = await apiFetch('/api/club/invitations', {
        method: 'POST',
        body: JSON.stringify({ email, team_id: teamId }),
      });
      setInvitedCoaches(prev => ({
        ...prev,
        [teamId]: [...(prev[teamId] || []), email],
      }));
      setCoachEmails(prev => ({ ...prev, [teamId]: '' }));
      setSuccess(data.linked ? data.message : `Invitation sent to ${email}.`);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!activeTeamForPlayers) return;
    setError('');
    if (!playerForm.first_name.trim() || !playerForm.last_name.trim()) { setError('First and last name are required'); return; }
    if (!playerForm.username.trim()) { setError('Username is required'); return; }
    if (!playerForm.password.trim()) { setError('Password is required'); return; }
    const team = createdTeams.find(t => t.id === activeTeamForPlayers);
    if (team?.has_under_13 && !playerForm.parent_email.trim()) { setError('Parent email is required for under-13 teams'); return; }
    setSaving(true);
    try {
      const { birth_year, ...pfRest } = playerForm;
      await apiFetch('/api/admin/players', {
        method: 'POST',
        headers: { 'x-team-id': activeTeamForPlayers },
        body: JSON.stringify({ ...pfRest, ...(birth_year ? { birth_year: parseInt(birth_year, 10) } : {}) }),
      });
      setAddedPlayers(prev => ({
        ...prev,
        [activeTeamForPlayers]: [...(prev[activeTeamForPlayers] || []), { ...playerForm }],
      }));
      setSuccess(`Player ${playerForm.first_name} ${playerForm.last_name} added.`);
      setPlayerForm({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Progress dots ──

  const getSteps = () => {
    if (path === 'import') return ['welcome', 'import'];
    return ['welcome', 'create-team', 'invite-coaches', 'add-players'];
  };

  const stepLabels = {
    'welcome': '1',
    'import': '2',
    'create-team': '2',
    'invite-coaches': '3',
    'add-players': '4',
  };

  const renderProgress = () => {
    if (step === 'done') return null;
    const steps = getSteps();
    const currentIdx = steps.indexOf(step);
    return (
      <div className="onboarding-progress" style={{ marginBottom: 24 }}>
        {steps.map((s, i) => (
          <div
            key={s}
            className={`onboarding-dot${i <= currentIdx ? ' active' : ''}${s === step ? ' current' : ''}`}
          >
            {stepLabels[s]}
          </div>
        ))}
      </div>
    );
  };

  // ── Summary stats ──

  const totalTeams = createdTeams.length;
  const totalCoaches = Object.values(invitedCoaches).reduce((sum, arr) => sum + arr.length, 0);
  const totalPlayers = Object.values(addedPlayers).reduce((sum, arr) => sum + arr.length, 0);

  // ── Clear messages on step change ──
  const goToStep = (s) => {
    setError('');
    setSuccess('');
    setStep(s);
  };

  // ── Render ──

  return (
    <div className="order-page">
      <header className="order-header">
        <div className="order-wrap">
          <div className="order-logo-row">
            <img src={LOGO} alt="Train Daily" className="order-logo" />
            <span className="order-brand">Train Daily</span>
          </div>
        </div>
      </header>
      <div className="order-wrap" style={{ maxWidth: 640, padding: '0 16px' }}>
        {renderProgress()}

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}

        {/* ── WELCOME STEP ── */}
        {step === 'welcome' && (
          <div className="onboarding-card">
            <h2>Let's Get Started</h2>
            <p className="onboarding-subtitle">
              This setup takes 2-5 minutes and gets you started on the right foot. You can always add more teams and players later from your dashboard.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
              <button
                className="onboarding-path-btn"
                onClick={() => { setPath('import'); goToStep('import'); }}
              >
                <i className="fas fa-file-csv" style={{ fontSize: '1.5rem', color: 'var(--op-orange)' }}></i>
                <div>
                  <strong style={{ display: 'block', fontSize: '1rem', marginBottom: 2 }}>Import Rosters & Coaches</strong>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Upload a CSV with teams, coaches, and players</span>
                </div>
              </button>
              <button
                className="onboarding-path-btn"
                onClick={() => { setPath('manual'); goToStep('create-team'); }}
              >
                <i className="fas fa-users" style={{ fontSize: '1.5rem', color: 'var(--blue)' }}></i>
                <div>
                  <strong style={{ display: 'block', fontSize: '1rem', marginBottom: 2 }}>Add Coaches & Players Manually</strong>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Create teams and add people one by one</span>
                </div>
              </button>
            </div>
            <button className="onboarding-skip" onClick={handleComplete}>Skip for now</button>
          </div>
        )}

        {/* ── IMPORT STEP ── */}
        {step === 'import' && !importResult && (
          <div className="onboarding-card">
            <h2>Import Rosters & Coaches</h2>
            <p className="onboarding-subtitle">
              Upload a CSV to create teams, coaches, and players in one pass. Columns (comma-separated):
            </p>
            <code style={{ display: 'block', fontSize: '0.8rem', background: '#f1f5f9', padding: 10, borderRadius: 6, marginBottom: 12, color: 'var(--op-orange)' }}>
              Team Name, Under 13?, Coach or Player, First Name, Last Name, Email
            </code>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 12 }}>
              Email is required for coaches. For players on under-13 teams, email is the parent email for consent. A header row is optional.
            </p>
            <div style={{ marginBottom: 12 }}>
              <button className="btn btn-orange btn-sm" onClick={downloadSampleCSV} style={{ marginBottom: 8 }}>Download Sample CSV</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <input type="file" accept=".csv,.txt" onChange={handleFileUpload} style={{ marginBottom: 8 }} />
            </div>
            <textarea
              className="onboarding-input"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="Or paste CSV data here..."
              rows={8}
              style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            />
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="btn btn-blue btn-sm" onClick={handleImportPreview} disabled={saving || !csvText.trim()}>
                {saving ? 'Validating...' : 'Preview Import'}
              </button>
              {importPreview && importPreview.summary.errors.length === 0 && (
                <button className="btn btn-orange btn-sm" onClick={handleImportCommit} disabled={saving}>
                  {saving ? 'Importing...' : 'Commit Import'}
                </button>
              )}
            </div>

            {importPreview && (
              <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
                <h3 style={{ fontSize: '0.95rem', marginBottom: 8 }}>Preview</h3>
                <div style={{ fontSize: '0.85rem', marginBottom: 8 }}>
                  <div>Teams to create: <strong>{importPreview.summary.teams_to_create}</strong></div>
                  <div>Existing teams: <strong>{importPreview.summary.teams_existing}</strong></div>
                  <div>Coaches: <strong>{importPreview.summary.coaches}</strong></div>
                  <div>Players: <strong>{importPreview.summary.players}</strong></div>
                  {importPreview.summary.duplicates_skipped > 0 && (
                    <div style={{ color: '#6c757d' }}>Existing players skipped: <strong>{importPreview.summary.duplicates_skipped}</strong></div>
                  )}
                </div>
                {importPreview.errors.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {importPreview.errors.map((err, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', color: err.error ? '#e74c3c' : '#f39c12', marginBottom: 4 }}>
                        Row {err.row}: {err.error || err.warning}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="onboarding-skip" onClick={() => goToStep('welcome')} style={{ marginTop: 0 }}>Back</button>
              <button className="onboarding-skip" onClick={handleComplete} style={{ marginTop: 0 }}>Skip for now</button>
            </div>
          </div>
        )}

        {/* ── IMPORT DONE ── */}
        {step === 'import' && importResult && (
          <div className="onboarding-card">
            <h2 style={{ color: '#16a34a' }}>Import Complete</h2>
            <div style={{ fontSize: '0.95rem', marginBottom: 16 }}>
              <div>Teams created: <strong>{importResult.summary.teams_created}</strong></div>
              <div>Coaches processed: <strong>{importResult.summary.coaches_processed}</strong></div>
              <div>Players created: <strong>{importResult.summary.players_created}</strong></div>
            </div>
            {importResult.credentials.length > 0 && (
              <button className="btn btn-blue btn-sm" onClick={() => downloadCredentials(importResult.credentials)} style={{ marginBottom: 16 }}>
                Download Player Logins
              </button>
            )}
            <button className="btn btn-orange" onClick={handleComplete} style={{ width: '100%', marginTop: 8 }}>
              Go to Dashboard
            </button>
          </div>
        )}

        {/* ── CREATE TEAM STEP ── */}
        {step === 'create-team' && (
          <div className="onboarding-card">
            <h2>{createdTeams.length === 0 ? 'Create Your First Team' : 'Add Another Team'}</h2>
            <p className="onboarding-subtitle">
              Teams group your players by age or skill level. You can create more teams later.
            </p>
            <form onSubmit={handleCreateTeam}>
              <div className="onboarding-field">
                <label>Team Name</label>
                <input
                  className="onboarding-input"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. U10 Lightning"
                  required
                />
              </div>
              <div className="onboarding-field">
                <label>Age Group</label>
                <select
                  className="onboarding-input"
                  value={teamForm.age_group}
                  onChange={(e) => setTeamForm(prev => ({ ...prev, age_group: e.target.value }))}
                  required
                >
                  <option value="">Select age group</option>
                  {ageGroups.map(ag => <option key={ag} value={ag}>{ag}</option>)}
                </select>
              </div>
              <div className="onboarding-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={teamForm.has_under_13}
                    onChange={(e) => setTeamForm(prev => ({ ...prev, has_under_13: e.target.checked }))}
                  />
                  This team has players under 13 (requires parental consent)
                </label>
              </div>
              <button className="btn btn-orange" type="submit" disabled={saving} style={{ width: '100%' }}>
                {saving ? 'Creating...' : 'Create Team'}
              </button>
            </form>

            {createdTeams.length > 0 && (
              <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                <h3 style={{ fontSize: '0.9rem', marginBottom: 8, color: '#334155' }}>Teams Created</h3>
                {createdTeams.map(t => (
                  <div key={t.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.9rem' }}>
                    <strong>{t.name}</strong> <span style={{ color: '#64748b' }}>({t.age_group})</span>
                  </div>
                ))}
                <button
                  className="btn btn-blue"
                  onClick={() => goToStep('invite-coaches')}
                  style={{ width: '100%', marginTop: 16 }}
                >
                  Continue to Coaches
                </button>
              </div>
            )}

            <button className="onboarding-skip" onClick={handleComplete}>Skip for now</button>
          </div>
        )}

        {/* ── INVITE COACHES STEP ── */}
        {step === 'invite-coaches' && (
          <div className="onboarding-card">
            <h2>Invite Coaches</h2>
            <p className="onboarding-subtitle">
              Enter email addresses for each team's coaches. They'll get an invite to create their account.
            </p>
            {createdTeams.map(team => (
              <div key={team.id} style={{ marginBottom: 20, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
                <strong style={{ display: 'block', marginBottom: 8 }}>{team.name}</strong>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="onboarding-input"
                    type="email"
                    placeholder="Coach email address"
                    value={coachEmails[team.id] || ''}
                    onChange={(e) => setCoachEmails(prev => ({ ...prev, [team.id]: e.target.value }))}
                    style={{ flex: 1, marginBottom: 0 }}
                  />
                  <button
                    className="btn btn-orange btn-sm"
                    onClick={() => handleInviteCoach(team.id)}
                    disabled={saving || !(coachEmails[team.id] || '').trim()}
                  >
                    Invite
                  </button>
                </div>
                {(invitedCoaches[team.id] || []).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {invitedCoaches[team.id].map((email, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', color: '#16a34a', padding: '4px 0' }}>
                        <i className="fas fa-check" style={{ marginRight: 6 }}></i>{email}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button
              className="btn btn-blue"
              onClick={() => {
                if (createdTeams.length > 0) setActiveTeamForPlayers(createdTeams[0].id);
                goToStep('add-players');
              }}
              style={{ width: '100%' }}
            >
              Continue to Players
            </button>
            <button className="onboarding-skip" onClick={handleComplete}>Skip for now</button>
          </div>
        )}

        {/* ── ADD PLAYERS STEP ── */}
        {step === 'add-players' && (
          <div className="onboarding-card">
            <h2>Add Players</h2>
            <p className="onboarding-subtitle">
              Add players to your teams. You can also do this later from your dashboard.
            </p>

            {/* Team selector */}
            {createdTeams.length > 1 && (
              <div className="onboarding-field">
                <label>Team</label>
                <select
                  className="onboarding-input"
                  value={activeTeamForPlayers || ''}
                  onChange={(e) => setActiveTeamForPlayers(e.target.value)}
                >
                  {createdTeams.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({(addedPlayers[t.id] || []).length} player{(addedPlayers[t.id] || []).length !== 1 ? 's' : ''})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {activeTeamForPlayers && (
              <form onSubmit={handleAddPlayer}>
                <div className="onboarding-row">
                  <div className="onboarding-field">
                    <label>First Name</label>
                    <input
                      className="onboarding-input"
                      value={playerForm.first_name}
                      onChange={(e) => setPlayerForm(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="onboarding-field">
                    <label>Last Name</label>
                    <input
                      className="onboarding-input"
                      value={playerForm.last_name}
                      onChange={(e) => setPlayerForm(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="onboarding-field">
                  <label>Birth Year <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                  <select className="onboarding-input" value={playerForm.birth_year} onChange={(e) => setPlayerForm(prev => ({ ...prev, birth_year: e.target.value }))}>
                    <option value="">--</option>
                    {Array.from({ length: 23 }, (_, i) => { const y = new Date().getFullYear() - 3 - i; return <option key={y} value={y}>{y}</option>; })}
                  </select>
                </div>
                <div className="onboarding-row">
                  <div className="onboarding-field">
                    <label>Username</label>
                    <input
                      className="onboarding-input"
                      value={playerForm.username}
                      onChange={(e) => setPlayerForm(prev => ({ ...prev, username: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="onboarding-field">
                    <label>Password</label>
                    <input
                      className="onboarding-input"
                      type="text"
                      value={playerForm.password}
                      onChange={(e) => setPlayerForm(prev => ({ ...prev, password: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                {createdTeams.find(t => t.id === activeTeamForPlayers)?.has_under_13 && (
                  <div className="onboarding-field">
                    <label>Parent Email</label>
                    <input
                      className="onboarding-input"
                      type="email"
                      value={playerForm.parent_email}
                      onChange={(e) => setPlayerForm(prev => ({ ...prev, parent_email: e.target.value }))}
                      placeholder="Required for under-13 teams"
                      required
                    />
                  </div>
                )}
                <button className="btn btn-orange" type="submit" disabled={saving} style={{ width: '100%' }}>
                  {saving ? 'Adding...' : 'Add Player'}
                </button>
              </form>
            )}

            {/* Added players list */}
            {activeTeamForPlayers && (addedPlayers[activeTeamForPlayers] || []).length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                <h3 style={{ fontSize: '0.85rem', color: '#334155', marginBottom: 8 }}>
                  Players Added to {createdTeams.find(t => t.id === activeTeamForPlayers)?.name}
                </h3>
                {addedPlayers[activeTeamForPlayers].map((p, i) => (
                  <div key={i} style={{ fontSize: '0.85rem', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                    {p.first_name} {p.last_name} <span style={{ color: '#64748b' }}>({p.username})</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
              <button
                className="btn btn-outline"
                onClick={() => goToStep('create-team')}
                style={{ flex: 1 }}
              >
                + Add Another Team
              </button>
              <button
                className="btn btn-blue"
                onClick={() => goToStep('done')}
                style={{ flex: 1 }}
              >
                Finish Setup
              </button>
            </div>
            <button className="onboarding-skip" onClick={handleComplete}>Skip for now</button>
          </div>
        )}

        {/* ── DONE STEP ── */}
        {step === 'done' && (
          <div className="onboarding-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>&#127881;</div>
            <h2>You're All Set!</h2>
            <p className="onboarding-subtitle">Here's what you set up:</p>
            <div style={{ fontSize: '1rem', marginBottom: 24, lineHeight: 1.8 }}>
              <div><strong>{totalTeams}</strong> team{totalTeams !== 1 ? 's' : ''} created</div>
              <div><strong>{totalCoaches}</strong> coach{totalCoaches !== 1 ? 'es' : ''} invited</div>
              <div><strong>{totalPlayers}</strong> player{totalPlayers !== 1 ? 's' : ''} added</div>
            </div>
            <button className="btn btn-orange" onClick={handleComplete} style={{ width: '100%', padding: '14px 24px', fontSize: '1.05rem' }}>
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
